import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Image } from "./Image";
import { Heading } from "./Heading";
import { Price } from "./Price";
import { Link } from "./Link";
import { Badge } from "./Badge";
import { getImageUrl } from "../../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../../hooks/useStoreLogoPlaceholder";
import { VariantSelectionModal } from "./VariantSelectionModal";
import { useCart } from "../../context/CartContext";
import { useStore } from "../../context/StoreContext";
import { useTranslatedValue } from "../../hooks/useTranslatedValue";
import { useTranslation } from "react-i18next";
import { useCartAnimationContext } from "../../context/CartAnimationContext";
import { Spinner } from "./Spinner";
import { calculateProductCardPricing } from "../../utils/inventoryUtils";

export interface ProductCardProps {
  id: string;
  title: string;
  price?: number;
  originalPrice?: number;
  image: string;
  currency?: string;
  description?: string;
  badge?: string;
  badgeVariant?: "default" | "primary" | "success" | "warning" | "error";
  onAddToCart?: (id: string) => void;
  href?: string;
  className?: string;
  isOutOfStock?: boolean;
  inventoryQuantity?: number | null;
  locationId?: string;
  product?: any;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  id,
  title,
  price: propPrice,
  originalPrice: propOriginalPrice,
  image,
  currency = "USD",
  badge,
  badgeVariant = "primary",
  onAddToCart,
  href,
  className = "",
  isOutOfStock = false,
  inventoryQuantity,
  locationId,
  product,
}) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { isPluginEnabled } = useStore();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0])); // Track which images have been loaded
  const { triggerAnimation } = useCartAnimationContext();
  const addButtonRef = useRef<HTMLButtonElement>(null);

  // Get translated title if product has translations
  // Hook must be called unconditionally - pass null if product doesn't exist
  const translatedTitleValue = useTranslatedValue(product || null, "title");
  const translatedTitle =
    product && translatedTitleValue ? translatedTitleValue : title;

  // Prepare product images array
  const productImages =
    product?.images && product.images.length > 0
      ? product.images.map((img: any) =>
          getImageUrl(img?.url, storeLogoPlaceholder),
        )
      : [];

  // Use store logo as placeholder for missing images
  const imageUrl =
    image && image !== "/placeholder.png" && image !== "/logo512.png"
      ? getImageUrl(image, storeLogoPlaceholder)
      : storeLogoPlaceholder;

  // Get all available images (product images or fallback to single image)
  const allImages = productImages.length > 0 ? productImages : [imageUrl];
  const hasMultipleImages = allImages.length > 1;

  // Find the index of the current image being displayed
  let displayedImageIndex: number | undefined;
  if (product?.images && product.images.length > 0 && image) {
    // Find the index of the current image in the product's images array
    const currentImage = product.images.find((img: any) => img.url === image);
    if (currentImage) {
      displayedImageIndex = product.images.indexOf(currentImage);
    }
  }
  // Calculate pricing with discounts if product is provided
  const pricing = useMemo(() => {
    return product && locationId
      ? calculateProductCardPricing(
          product,
          locationId,
          isPluginEnabled,
          displayedImageIndex,
        )
      : { price: propPrice || 0, originalPrice: propOriginalPrice };
  }, [product, locationId, isPluginEnabled, displayedImageIndex]);
  const { price, originalPrice } = pricing;

  // Reset image index and loaded images when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setLoadedImages(new Set([0])); // Always keep primary image as loaded
  }, [product?._id]);

  // Handle image switching on hover - switch to next image immediately
  useEffect(() => {
    if (!isHovered || !hasMultipleImages) {
      setCurrentImageIndex(0);
      return;
    }

    // Switch to next image (index 1, or wrap to 0 if only 2 images)
    setCurrentImageIndex(1);
  }, [isHovered, hasMultipleImages]);

  // Track when images are loaded - keep them in memory once loaded
  const handleImageLoad = useCallback((index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  }, []);

  // Check if product has variant options
  const hasVariantOptions =
    product?.variantOptions && product.variantOptions.length > 0;

  const handleAddClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't block digital products from being added to cart
    if (isOutOfStock && !product?.isDigital) return;

    // If product has variant options, show modal
    if (hasVariantOptions && product) {
      setShowVariantModal(true);
    } else {
      // Add to cart directly and trigger animation
      try {
        setIsAddingToCart(true);
        // Call onAddToCart callback if provided, otherwise use context addToCart
        if (onAddToCart) {
          onAddToCart(id);
        } else {
          await addToCart(id, 1);
        }
        // Trigger animation after adding to cart
        setTimeout(() => {
          if (addButtonRef.current) {
            triggerAnimation(addButtonRef.current);
          }
          else {
            console.error("Add button reference not found");
          }
        }, 100);
      } catch (error) {
        console.error("Error adding to cart:", error);
      } finally {
        setIsAddingToCart(false);
      }
    }
  };

  const handleAddToCartFromModal = async (
    productId: string,
    quantity: number,
    selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>,
  ) => {
    try {
      setIsAddingToCart(true);
      // Always use quantity 1 for digital products
      const finalQuantity = product?.isDigital ? 1 : quantity;
      await addToCart(
        productId,
        finalQuantity,
        undefined,
        selectedVariantOptions,
      );
      // Trigger animation after adding to cart and modal closes
      // Small delay to ensure modal has closed and button is visible
      setTimeout(() => {
        if (addButtonRef.current) {
          triggerAnimation(addButtonRef.current);
        }
      }, 300);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const content = (
    <div
      className={`group/product-card bg-white border border-store-accent-light transition-all duration-300 overflow-hidden cursor-pointer flex flex-col h-full ${className}`}
      style={{
        willChange: "transform",
        backfaceVisibility: "hidden",
        borderRadius: "var(--store-border-radius, 0.375rem)",
        boxShadow:
          "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
        borderWidth: "var(--store-border-width, 2px)",
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        setIsHovered(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setIsHovered(false);
        setCurrentImageIndex(0);
      }}
    >
      {/* Image Container */}
      <div
        className="relative overflow-hidden bg-store-background aspect-square flex items-center justify-center"
        style={{ minHeight: 0 }}
      >
        {allImages.map((img: any, index: number) => {
          // Primary image (index 0): always available, lazy load when entering viewport
          // Secondary images (index > 0):
          //   - Load on hover OR if already loaded before
          //   - Once loaded, always keep src to stay in DOM (only opacity changes on hover)
          const isPrimary = index === 0;
          const isLoaded = loadedImages.has(index);
          const shouldLoadImage =
            isPrimary || isLoaded || (index > 0 && isHovered);
          const imageSrc = shouldLoadImage ? img : undefined;

          return (
            <Image
              key={`${product?._id || id}-img-${index}`}
              src={imageSrc}
              alt={title}
              fallback={storeLogoPlaceholder}
              placeholder={storeLogoPlaceholder}
              aspectRatio="square"
              objectFit="cover"
              useLazyLoading={isPrimary}
              rootMargin="100px"
              onLoad={() => handleImageLoad(index)}
              className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${
                index === currentImageIndex ? "opacity-100" : "opacity-0"
              }`}
              style={{
                display: "block",
                height: "100%",
                width: "100%",
                objectFit: "cover",
              }}
            />
          );
        })}
        {isOutOfStock && !product?.isDigital && (
          <div className="absolute top-2 right-2 z-10">
            <Badge variant="error" size="sm">
              {t("products.outOfStock")}
            </Badge>
          </div>
        )}
        {badge && !isOutOfStock && (
          <div className="absolute top-2 right-2 z-10">
            <Badge variant={badgeVariant}>{badge}</Badge>
          </div>
        )}
      </div>

      {/* Content - Compact */}
      <div className="p-3 flex flex-col flex-grow">
        <Heading
          size="sm"
          weight="semibold"
          className="mb-1 line-clamp-2 text-store-primary group-hover/product-card:text-store-secondary transition-colors text-sm flex-shrink-0"
        >
          {translatedTitle}
        </Heading>

        {/* Price and Inventory - Compact */}
        <div className="space-y-1 mt-auto">
          <div className="flex items-center justify-between">
            <Price
              amount={price}
              originalAmount={originalPrice}
              currency={currency}
              size="md"
              className="text-store-secondary font-bold text-sm"
            />
            {(onAddToCart || hasVariantOptions) && (
              <button
                ref={addButtonRef}
                onClick={handleAddClick}
                disabled={
                  (isOutOfStock && !product?.isDigital) || isAddingToCart
                }
                className={`px-4 py-2 text-white text-sm font-semibold transition-colors flex items-center justify-center min-w-[60px] ${
                  (isOutOfStock && !product?.isDigital) || isAddingToCart
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-store-primary hover:opacity-90"
                }`}
                style={{
                  borderRadius: "var(--store-border-radius, 0.375rem)",
                  boxShadow:
                    "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
                }}
              >
                {isAddingToCart ? (
                  <span className="flex items-center">
                    <Spinner size="sm" />
                    <span>Adding...</span>
                  </span>
                ) : (
                  "Add"
                )}
              </button>
            )}
          </div>
          {!product?.isDigital &&
            inventoryQuantity !== null &&
            inventoryQuantity !== undefined &&
            (isOutOfStock ||
              (inventoryQuantity > 0 && inventoryQuantity < 10)) && (
              <div
                className={`text-xs ${isOutOfStock ? "text-red-600 font-semibold" : "text-gray-600"}`}
              >
                {isOutOfStock ? t("products.outOfStock") : ``}
              </div>
            )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {href ? (
        <Link to={href} variant="default" className="block h-full">
          {content}
        </Link>
      ) : (
        content
      )}

      {/* Variant Selection Modal */}
      {hasVariantOptions && product && (
        <VariantSelectionModal
          isOpen={showVariantModal}
          onClose={() => setShowVariantModal(false)}
          product={product}
          onAddToCart={handleAddToCartFromModal}
        />
      )}
    </>
  );
};

export default ProductCard;
